/**
 * Delta patching utilities for state synchronization.
 *
 * Computes minimal diffs between states and applies patches.
 */

import type {DeepPartial, DeltaPatch, SharedState} from './sync-types.ts'

/**
 * Symbol used to mark explicit deletions in patches.
 * When serialized to JSON, this becomes `null` with a special marker.
 */
const DELETE_MARKER = '__$$DELETE$$__'

/**
 * Check if a value is a plain object (not array, null, etc.)
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Check if an object has an 'id' property (for array item matching)
 */
function hasId(value: unknown): value is {id: string} {
	return isPlainObject(value) && typeof value.id === 'string'
}

/**
 * Deep equality check
 */
export function deepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true
	if (a === null || b === null) return a === b
	if (typeof a !== typeof b) return false

	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false
		return a.every((item, i) => deepEqual(item, b[i]))
	}

	if (isPlainObject(a) && isPlainObject(b)) {
		const keysA = Object.keys(a)
		const keysB = Object.keys(b)
		if (keysA.length !== keysB.length) return false
		return keysA.every(key => deepEqual(a[key], b[key]))
	}

	return false
}

/**
 * Compute the delta between two arrays of objects with IDs.
 * Returns an array that, when merged with the old array, produces the new array.
 *
 * Strategy:
 * - Items with same ID: include only if changed (with partial changes)
 * - New items: include in full
 * - Removed items: mark with DELETE_MARKER
 */
function computeArrayDelta<T extends {id: string}>(
	oldArray: readonly T[],
	newArray: readonly T[],
): (DeepPartial<T> | {id: string; __deleted: true})[] | undefined {
	const oldMap = new Map(oldArray.map(item => [item.id, item]))
	const newMap = new Map(newArray.map(item => [item.id, item]))

	const changes: (DeepPartial<T> | {id: string; __deleted: true})[] = []
	let hasChanges = false

	// Check for new or modified items
	for (const newItem of newArray) {
		const oldItem = oldMap.get(newItem.id)
		if (!oldItem) {
			// New item - include in full
			changes.push(newItem as DeepPartial<T>)
			hasChanges = true
		} else {
			// Existing item - compute object delta
			const itemDelta = computeObjectDelta(oldItem, newItem)
			if (itemDelta !== undefined) {
				// Include id so we know which item to update
				changes.push({id: newItem.id, ...itemDelta} as DeepPartial<T>)
				hasChanges = true
			}
		}
	}

	// Check for deleted items
	for (const oldItem of oldArray) {
		if (!newMap.has(oldItem.id)) {
			changes.push({id: oldItem.id, __deleted: true} as {id: string; __deleted: true})
			hasChanges = true
		}
	}

	return hasChanges ? changes : undefined
}

/**
 * Compute the delta between two plain objects.
 * Returns an object containing only the changed keys.
 */
function computeObjectDelta<T extends Record<string, unknown>>(oldObj: T, newObj: T): DeepPartial<T> | undefined {
	const changes: Record<string, unknown> = {}
	let hasChanges = false

	// Find changed and new keys
	for (const key of Object.keys(newObj)) {
		const oldVal = oldObj[key]
		const newVal = newObj[key]

		if (deepEqual(oldVal, newVal)) {
			continue
		}

		hasChanges = true

		// Recurse into nested objects
		if (isPlainObject(oldVal) && isPlainObject(newVal)) {
			changes[key] = computeObjectDelta(oldVal, newVal) ?? newVal
		}
		// Handle arrays of items with IDs
		else if (
			Array.isArray(oldVal) &&
			Array.isArray(newVal) &&
			oldVal.length > 0 &&
			hasId(oldVal[0]) &&
			newVal.length > 0 &&
			hasId(newVal[0])
		) {
			const arrayDelta = computeArrayDelta(oldVal as {id: string}[], newVal as {id: string}[])
			if (arrayDelta) {
				changes[key] = arrayDelta
			}
		}
		// Handle array becoming empty
		else if (Array.isArray(oldVal) && Array.isArray(newVal) && newVal.length === 0) {
			changes[key] = []
		}
		// Simple value change
		else {
			changes[key] = newVal
		}
	}

	// Find deleted keys
	for (const key of Object.keys(oldObj)) {
		if (!(key in newObj)) {
			changes[key] = DELETE_MARKER
			hasChanges = true
		}
	}

	return hasChanges ? (changes as DeepPartial<T>) : undefined
}

/**
 * Compute the delta patch between two shared states.
 * Returns undefined if states are identical.
 */
export function computeDelta(oldState: SharedState, newState: SharedState): DeltaPatch | undefined {
	const changes = computeObjectDelta(
		oldState as unknown as Record<string, unknown>,
		newState as unknown as Record<string, unknown>,
	)

	if (!changes) {
		return undefined
	}

	return {
		timestamp: Date.now(),
		changes: changes as DeepPartial<SharedState>,
	}
}

/**
 * Check if a delta patch has any actual changes
 */
export function hasChanges(patch: DeltaPatch | undefined): patch is DeltaPatch {
	if (!patch) return false
	return Object.keys(patch.changes).length > 0
}

/**
 * Apply an array delta to an existing array
 */
function applyArrayDelta<T extends {id: string}>(
	oldArray: readonly T[],
	delta: readonly (DeepPartial<T> | {id: string; __deleted?: true})[],
): T[] {
	const result = [...oldArray] as T[]

	for (const change of delta) {
		if (!hasId(change)) continue

		const existingIndex = result.findIndex(item => item.id === change.id)

		if ('__deleted' in change && change.__deleted) {
			// Delete item
			if (existingIndex >= 0) {
				result.splice(existingIndex, 1)
			}
		} else if (existingIndex >= 0) {
			// Update existing item
			const existing = result[existingIndex]
			if (existing) {
				result[existingIndex] = applyObjectDelta(
					existing as Record<string, unknown>,
					change as Record<string, unknown>,
				) as T
			}
		} else {
			// Add new item
			result.push(change as T)
		}
	}

	return result
}

/**
 * Apply an object delta to an existing object
 */
function applyObjectDelta(oldObj: Record<string, unknown>, delta: Record<string, unknown>): Record<string, unknown> {
	const result: Record<string, unknown> = {...oldObj}

	for (const [key, value] of Object.entries(delta)) {
		if (value === DELETE_MARKER) {
			delete result[key]
		} else if (value === undefined) {
			// Explicit undefined means delete
			delete result[key]
		} else if (isPlainObject(value) && isPlainObject(result[key])) {
			// Recurse into nested objects
			result[key] = applyObjectDelta(result[key] as Record<string, unknown>, value)
		} else if (Array.isArray(value) && Array.isArray(result[key])) {
			const oldArray = result[key] as unknown[]
			// Check if it's an ID-based array delta
			if (value.length > 0 && hasId(value[0]) && oldArray.length > 0 && hasId(oldArray[0])) {
				result[key] = applyArrayDelta(oldArray as {id: string}[], value as {id: string}[])
			} else {
				// Replace array entirely
				result[key] = value
			}
		} else {
			result[key] = value
		}
	}

	return result
}

/**
 * Apply a delta patch to a shared state, returning the new state.
 */
export function applyDelta(state: SharedState, patch: DeltaPatch): SharedState {
	return applyObjectDelta(
		state as unknown as Record<string, unknown>,
		patch.changes as unknown as Record<string, unknown>,
	) as unknown as SharedState
}

/**
 * Deep clone an object (simple JSON-based)
 */
export function deepClone<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj))
}
